import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const MAX_ATTEMPTS = 6;
const POLL_MS = 2000;

export default function PaymentReturn() {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | success | failed | expired
  const [programId, setProgramId] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setProgramId(data.program_id);
        if (data.payment_status === "paid") {
          setStatus("success");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) {
          setStatus("failed");
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch {
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) setStatus("failed");
        else setTimeout(poll, POLL_MS);
      }
    };
    poll();
  }, [location.search]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="payment-return">
      {status === "checking" && (
        <>
          <Loader2 className="mx-auto text-primary-ub animate-spin" size={40} />
          <h1 className="font-serif text-4xl mt-6">Confirming your payment…</h1>
          <p className="text-ub-2 mt-2">This takes a few seconds. Please stay on this page.</p>
        </>
      )}
      {status === "success" && (
        <>
          <CheckCircle2 className="mx-auto text-[var(--ub-secondary)]" size={48} />
          <h1 className="font-serif text-5xl mt-6">You're in.</h1>
          <p className="mt-3 text-ub-2 max-w-md mx-auto">Anant's team will reach out with your program details, materials and dates.</p>
          <div className="flex justify-center gap-3 mt-8">
            {programId && (
              <Link to={`/programs/${programId}`} className="rounded-full px-7 py-3.5 border border-ub-dark hover:bg-ub-dark hover:text-white transition-colors" data-testid="return-program-link">
                View program
              </Link>
            )}
            <button onClick={() => navigate("/")} className="bg-primary-ub text-white rounded-full px-7 py-3.5 hover:bg-[var(--ub-primary-hover)] transition-colors" data-testid="return-home-btn">
              Back home
            </button>
          </div>
        </>
      )}
      {status === "expired" && (
        <>
          <XCircle className="mx-auto text-[var(--ub-primary)]" size={48} />
          <h1 className="font-serif text-4xl mt-6">Session expired</h1>
          <p className="text-ub-2 mt-3">Please try the checkout again from the program page.</p>
          <button onClick={() => navigate("/programs")} className="mt-6 bg-primary-ub text-white rounded-full px-7 py-3.5 hover:bg-[var(--ub-primary-hover)] transition-colors">
            Back to programs
          </button>
        </>
      )}
      {status === "failed" && (
        <>
          <XCircle className="mx-auto text-[var(--ub-primary)]" size={48} />
          <h1 className="font-serif text-4xl mt-6">We couldn't verify the payment</h1>
          <p className="text-ub-2 mt-3">Please check your email for a Stripe confirmation, or try again.</p>
          <button onClick={() => navigate("/programs")} className="mt-6 bg-primary-ub text-white rounded-full px-7 py-3.5 hover:bg-[var(--ub-primary-hover)] transition-colors">
            Back to programs
          </button>
        </>
      )}
    </div>
  );
}
